import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { BsBroadcast, BsFire, BsFilter, BsTagFill, BsStarFill } from "react-icons/bs";
import useAuth from "../../contexts/auth";
import {
  getOfertasAoVivo,
  getCupons,
  deleteCupom,
  deleteOfertaAoVivo,
  updateOfertaAoVivo,
  addOfertaAoVivoToLive,
  clearOfertasAoVivo,
  setOfertaDestaque,
} from "../../services/api";
import OfertaCard from "./OfertaCard";
import OfertaModal from "./OfertaModal";
import CupomModal from "./CupomModal";
import { CATEGORIAS, MARCAS, normalize as norm, offerCategory, offerBrand } from "./offerPresentation";
import "./styles.scss";

const LIVE_INTERVAL_MS = 5000;
const PRICE_STEP = 50;
// No ambiente oficial a aba "Ofertas Quentes" fica escondida.
const HIDE_QUENTES = process.env.REACT_APP_HIDE_QUENTES === "true";
export default function OfertasAoVivo() {
  const { user } = useAuth();
  const isAdmin = user && user.type === "admin";
  const isPoster = user && user.type === "poster";
  // Poster (postador) tem o mesmo acesso de gestão que o admin.
  const canManage = isAdmin || isPoster;

  const [tab, setTab] = useState("live");
  const [showFilters, setShowFilters] = useState(false);
  const [copiedCupom, setCopiedCupom] = useState(null);
  const [query, setQuery] = useState("");
  const [cats, setCats] = useState(() => Object.fromEntries(CATEGORIAS.map((item) => [item, true])));
  const [marcas, setMarcas] = useState(() => Object.fromEntries(MARCAS.map((item) => [item, true])));
  const [pMin, setPMin] = useState(0);
  const [pMax, setPMax] = useState(6000);
  const [pMaxTouched, setPMaxTouched] = useState(false);

  const [offers, setOffers] = useState([]);
  const [cupons, setCupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [addedToLive, setAddedToLive] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("oavAddedToLive") || "[]");
    } catch (e) {
      return [];
    }
  });

  const [showOferta, setShowOferta] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showCupom, setShowCupom] = useState(false);
  const [editingCupom, setEditingCupom] = useState(null);

  // Teto do filtro = maior oferta presente, arredondado pro passo do slider
  // (assim o topo é alcançável e nunca esconde oferta por ser cara).
  const priceCeil = useMemo(() => {
    const max = offers.reduce((m, o) => Math.max(m, Number(o.offer_price) || 0), 0);
    return Math.max(6000, Math.ceil(max / PRICE_STEP) * PRICE_STEP);
  }, [offers]);

  // Ofertas Destaque: somente ofertas LIVE marcadas como favorito (`destaque`).
  const destaques = useMemo(
    () => offers.filter((o) => o.destaque && (o.board || "ao_vivo") === "ao_vivo"),
    [offers]
  );

  // Auto-scroll do carrossel de destaques (pausa no hover). Com as setas,
  // o automático para enquanto o mouse está na faixa.
  const destaqueTrackRef = useRef(null);
  const destaquePausedRef = useRef(false);
  useEffect(() => {
    const el = destaqueTrackRef.current;
    if (!el || destaques.length < 2) return undefined;
    let raf;
    // O browser arredonda scrollLeft para inteiro, então acumulamos a posição
    // numa variável própria (senão +0.3 por frame nunca sai do zero).
    let pos = el.scrollLeft;
    const step = () => {
      if (!destaquePausedRef.current && el.scrollWidth > el.clientWidth + 4) {
        const half = el.scrollWidth / 2;
        pos += 0.3;
        if (pos >= half) pos -= half; // loop contínuo (lista duplicada)
        el.scrollLeft = pos;
      } else {
        pos = el.scrollLeft;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [destaques.length]);

  const scrollDestaque = (dir) => {
    const el = destaqueTrackRef.current;
    if (!el) return;
    destaquePausedRef.current = true; // pausa o automático pra seta não "brigar"
    el.scrollBy({ left: Math.round(el.clientWidth * 0.8) * dir, behavior: "smooth" });
    window.clearTimeout(el._oavResume);
    el._oavResume = window.setTimeout(() => {
      destaquePausedRef.current = false;
    }, 2500);
  };

  useEffect(() => {
    if (!pMaxTouched) setPMax(priceCeil);
  }, [priceCeil, pMaxTouched]);

  const load = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setLoading(true);
    try {
      const [o, c] = await Promise.all([getOfertasAoVivo({ limit: 200 }), getCupons()]);
      const list = Array.isArray(o?.data) ? o.data : [];
      // A resposta é o retrato atual: inclui edições de selo e remoções.
      setOffers(list);
      setCupons(Array.isArray(c?.data) ? c.data : []);
      setLastSync(new Date());
    } catch (e) {
      /* mantém estado atual */
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") load({ silent: true });
    };
    const t = setInterval(refresh, LIVE_INTERVAL_MS);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [load]);

  const setQ = async (id, selo) => {
    setOffers((prev) => prev.map((o) => (o.id === id ? { ...o, selo } : o)));
    try {
      await updateOfertaAoVivo(id, { selo });
    } catch (e) {
      window.alert("Não foi possível salvar o selo. Atualize a página para conferir o estado salvo.");
    } finally {
      load({ silent: true });
    }
  };

  const setLive = async (o) => {
    try {
      await addOfertaAoVivoToLive(o.id);
      setAddedToLive((prev) => {
        const next = prev.includes(o.id) ? prev : [...prev, o.id];
        try {
          localStorage.setItem("oavAddedToLive", JSON.stringify(next));
        } catch (e) {
          /* ignore */
        }
        return next;
      });
      load({ silent: true });
    } catch (e) {
      window.alert("Não foi possível adicionar às Ofertas Live. Atualize a página para conferir.");
    }
  };

  const setDestaque = async (o) => {
    const next = !o.destaque;
    setOffers((prev) => prev.map((x) => (x.id === o.id ? { ...x, destaque: next } : x)));
    try {
      await setOfertaDestaque(o.id, next);
    } catch (e) {
      window.alert("Não foi possível atualizar o destaque.");
      load({ silent: true });
    }
  };

  const openEdit = (o) => {
    setEditing(o);
    setShowOferta(true);
  };

  const closeOferta = () => {
    setShowOferta(false);
    setEditing(null);
  };

  const toggleCat = (id) => setCats((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleMarca = (m) => setMarcas((prev) => ({ ...prev, [m]: !prev[m] }));

  const visible = useMemo(() => {
    const q = norm(query.trim());
    const catSelected = Object.keys(cats).filter((k) => cats[k]);
    const marcaSelected = Object.keys(marcas).filter((k) => marcas[k]);

    return offers
      .filter((o) =>
        tab === "live"
          ? (o.board || "ao_vivo") === "ao_vivo"
          : o.board === "quente"
      )
      .filter((o) => {
        if (!catSelected.includes(offerCategory(o))) return false;
        if (!marcaSelected.includes(offerBrand(o))) return false;
        const price = Number(o.offer_price) || 0;
        if (price > 0 && (price < pMin || price > pMax)) return false;
        if (q && !norm(o.name).includes(q) && !norm(o.cupom).includes(q)) return false;
        return true;
      });
  }, [offers, tab, query, cats, marcas, pMin, pMax]);

  const copyCupom = (codigo) => {
    if (navigator.clipboard) navigator.clipboard.writeText(codigo);
    setCopiedCupom(codigo);
    setTimeout(() => setCopiedCupom((v) => (v === codigo ? null : v)), 1500);
  };

  const removeCupom = async (c) => {
    if (!window.confirm(`Remover o cupom ${c.codigo}?`)) return;
    await deleteCupom(c.id);
    load({ silent: true });
  };

  const removeOferta = async (o) => {
    if (!window.confirm(`Excluir a oferta "${o.name}"?`)) return;
    await deleteOfertaAoVivo(o.id);
    setOffers((prev) => prev.filter((x) => x.id !== o.id));
  };

  const clearBoard = async () => {
    const isLive = tab === "live";
    const label = isLive ? "Ofertas Live" : "Ofertas Quentes";
    if (
      !window.confirm(
        `Apagar TODAS as ofertas de ${label}? Essa ação não pode ser desfeita.`
      )
    ) {
      return;
    }
    try {
      await clearOfertasAoVivo(isLive ? "ao_vivo" : "quente");
      setOffers((prev) =>
        prev.filter((o) => (isLive ? o.board !== "ao_vivo" : o.board !== "quente"))
      );
    } catch (e) {
      window.alert("Não foi possível apagar as ofertas.");
    }
  };

  return (
    <div className="ofertas-ao-vivo">
      <Helmet title="Ofertas Ao Vivo – Caçadores de Ofertas" />

      <Container className="oav-main">
        <Row>
          <Col xs={12} lg={3} xl={2} className={`oav-sidebar ${showFilters ? "is-open" : ""}`}>
            <aside className="oav-aside">
              <h3>Categorias</h3>
              <div className="oav-checks">
                {CATEGORIAS.map((c) => (
                  <label key={c} className="oav-chk">
                    <input
                      type="checkbox"
                      checked={!!cats[c]}
                      onChange={() => toggleCat(c)}
                    />
                    {c}
                  </label>
                ))}
              </div>

              <h3>Marcas</h3>
              <div className="oav-checks">
                {MARCAS.map((m) => (
                  <label key={m} className="oav-chk">
                    <input
                      type="checkbox"
                      checked={!!marcas[m]}
                      onChange={() => toggleMarca(m)}
                    />
                    {m}
                  </label>
                ))}
              </div>

              <h3>Preço</h3>
              <div className="oav-preco-head">
                <b>
                  R$ {pMin} - R$ {pMax}
                </b>
              </div>
              <div className="oav-range">
                <input
                  type="range"
                  min="0"
                  max={priceCeil}
                  step={PRICE_STEP}
                  value={pMin}
                  onChange={(e) => setPMin(Math.min(+e.target.value, pMax - PRICE_STEP))}
                />
                <input
                  type="range"
                  min="0"
                  max={priceCeil}
                  step={PRICE_STEP}
                  value={pMax}
                  onChange={(e) => {
                    setPMaxTouched(true);
                    setPMax(Math.max(+e.target.value, pMin + PRICE_STEP));
                  }}
                />
              </div>

              {canManage && tab === "live" && (
                <Button
                  variant="primary"
                  className="w-100 mt-3"
                  onClick={() => setShowOferta(true)}
                >
                  Adicionar Oferta
                </Button>
              )}
            </aside>
          </Col>

          <Col xs={12} lg={9} xl={10} className="oav-content">
            <section className="oav-cupons-box">
              <h3 className="oav-cupons-title">
                <BsTagFill /> Cupons Ativos
              </h3>
              <div className="oav-cupons-row">
                {canManage && (
                  <Button
                    variant="primary"
                    size="sm"
                    className="oav-add-cupom"
                    onClick={() => {
                      setEditingCupom(null);
                      setShowCupom(true);
                    }}
                  >
                    Adicionar Cupom +
                  </Button>
                )}
                <div className="oav-cupons">
                  {cupons.map((c) => (
                    <span
                      key={c.id}
                      className={`oav-cupom ${c.link ? "com-link" : ""} ${
                        c.cores && c.cores[0] ? `is-${c.cores[0]}` : ""
                      }`}
                    >
                      {c.link ? (
                        <a
                          href={c.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => copyCupom(c.codigo)}
                          title="Copia o cupom e abre o site"
                        >
                          {copiedCupom === c.codigo ? "Copiado!" : c.codigo}
                        </a>
                      ) : (
                        <button
                          type="button"
                          className="oav-cupom-copy"
                          onClick={() => copyCupom(c.codigo)}
                          title="Clique para copiar o cupom"
                        >
                          {copiedCupom === c.codigo ? "Copiado!" : c.codigo}
                        </button>
                      )}
                      {canManage && (
                        <button
                          type="button"
                          className="oav-cupom-edit"
                          onClick={() => {
                            setEditingCupom(c);
                            setShowCupom(true);
                          }}
                          title="Editar cupom"
                        >
                          ✏️
                        </button>
                      )}
                      {canManage && (
                        <button
                          type="button"
                          className="x"
                          onClick={() => removeCupom(c)}
                          title="Remover"
                        >
                          ×
                        </button>
                      )}
                    </span>
                  ))}
                  {cupons.length === 0 && (
                    <span className="text-muted small">Nenhum cupom</span>
                  )}
                </div>
              </div>
            </section>

            {destaques.length > 0 && (
              <section className="oav-destaque">
                <h3 className="oav-destaque-title">
                  <BsStarFill /> Ofertas Destaque
                </h3>
                <div className="oav-destaque-wrap">
                  <button
                    type="button"
                    className="oav-destaque-arrow left"
                    onClick={() => scrollDestaque(-1)}
                    aria-label="Anterior"
                  >
                    ‹
                  </button>
                  <div
                    className={`oav-destaque-track ${
                      destaques.length >= 2 ? "is-marquee" : ""
                    }`}
                    ref={destaqueTrackRef}
                    onMouseEnter={() => {
                      destaquePausedRef.current = true;
                    }}
                    onMouseLeave={() => {
                      destaquePausedRef.current = false;
                    }}
                  >
                    {(destaques.length >= 2
                      ? [...destaques, ...destaques]
                      : destaques
                    ).map((o, i) => (
                      <div className="oav-destaque-item" key={`${o.id}-${i}`}>
                        <OfertaCard
                          product={o}
                          onQuality={setQ}
                          onDelete={removeOferta}
                          onEdit={openEdit}
                          onLive={setLive}
                          onDestaque={setDestaque}
                          showLive={false}
                          addedToLive={addedToLive}
                          isAdmin={canManage}
                        />
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="oav-destaque-arrow right"
                    onClick={() => scrollDestaque(1)}
                    aria-label="Próximo"
                  >
                    ›
                  </button>
                </div>
              </section>
            )}

            <hr className="oav-divider" />

            <div className="oav-toolbar">
              <div className="oav-toolbar-left">
                <button
                  type="button"
                  className="oav-filters-toggle"
                  onClick={() => setShowFilters((v) => !v)}
                >
                  <BsFilter /> Filtros {showFilters ? "▲" : "▼"}
                </button>
                <div className="oav-search">
                  <Form.Control
                    type="search"
                    placeholder="Pesquisar..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="oav-tabs" role="tablist">
                <button
                  type="button"
                  className={`oav-tab ${tab === "live" ? "is-active" : ""}`}
                  onClick={() => setTab("live")}
                >
                  <BsBroadcast /> Ofertas Live
                </button>
                {!HIDE_QUENTES && (
                  <button
                    type="button"
                    className={`oav-tab ${tab === "quentes" ? "is-active" : ""}`}
                    onClick={() => setTab("quentes")}
                  >
                    <BsFire /> Ofertas Quentes
                  </button>
                )}
              </div>
              {canManage && (
                <button
                  type="button"
                  className="oav-clear-btn"
                  onClick={clearBoard}
                  title={`Apagar todas as ofertas de ${
                    tab === "live" ? "Ofertas Live" : "Ofertas Quentes"
                  }`}
                >
                  🗑 Limpar {tab === "live" ? "Live" : "Quentes"}
                </button>
              )}
              <div className="oav-live-indicator">
                <span className="dot" />
                {lastSync ? `Ao vivo · ${lastSync.toLocaleTimeString("pt-BR")}` : "Conectando..."}
              </div>
            </div>

            {canManage && (
              <div className="oav-admin-banner">
                <b>Painel Administrador:</b> adicione ofertas, use o lápis para
                editar ou mude o selo de qualidade.
              </div>
            )}

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            ) : visible.length === 0 ? (
              <div className="oav-empty">
                <b>Nenhuma oferta encontrada</b>
                <p className="text-muted">
                  Ajuste os filtros ou mude de aba. Ofertas novas aparecem
                  automaticamente.
                </p>
              </div>
            ) : (
              <div className="oav-grid">
                {visible.map((o) => (
                  <OfertaCard
                    key={o.id}
                    product={o}
                    onQuality={setQ}
                    onDelete={removeOferta}
                    onEdit={openEdit}
                    onLive={setLive}
                    onDestaque={setDestaque}
                    showLive={tab === "quentes"}
                    addedToLive={addedToLive}
                    isAdmin={canManage}
                  />
                ))}
              </div>
            )}
          </Col>
        </Row>
      </Container>

      <OfertaModal
        show={showOferta}
        onHide={closeOferta}
        oferta={editing}
        onSaved={(result) => {
          if (result?.warning) window.alert(result.warning);
          closeOferta();
          load();
        }}
      />
      <CupomModal
        show={showCupom}
        cupom={editingCupom}
        onHide={() => {
          setShowCupom(false);
          setEditingCupom(null);
        }}
        onSaved={() => {
          setShowCupom(false);
          setEditingCupom(null);
          load({ silent: true });
        }}
      />
    </div>
  );
}

